import { refreshBenefitQueries } from '../sponsorship/benefitQueries';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Popover } from '@mui/material';
import { FiChevronRight } from 'react-icons/fi';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import PageLayout from '../../components/layout/PageLayout';
import MyProfileHero from '../../pages/mypage/components/MyProfileHero';
import PlaylistEditorGuard from '../../pages/playlist/PlaylistEditorGuard';
import ProfileImageCrop from './ProfileImageCrop';
import {
  profileDraft,
  profilePayload,
  validNickname,
  limitNickname,
  nicknameCharacters,
  MAX_PROFILE_IMAGE_BYTES,
} from './profileEditModel';
import baseline from '../../app-ui/baseline';
import copy from './editorCopy.json';
import palettes from './nicknameColors.json';
import './ProfileEditPage.css';
const unwrap = (r) => r?.data?.data ?? r?.data;
const token = () => localStorage.getItem('POSTPOP_AUTH_TOKEN');
export default function ProfileEditPage() {
  const user = useUserStore((s) => s.user),
    loading = useUserStore((s) => s.loading);
  const [session, setSession] = useState(0);
  const credential = useRef(token());
  useEffect(
    () =>
      useUserStore.subscribe((n, p) => {
        if (
          n.user?.id !== p.user?.id ||
          n.isLogin !== p.isLogin ||
          n.isAdmin !== p.isAdmin ||
          credential.current !== token()
        ) {
          credential.current = token();
          setSession((v) => v + 1);
        }
      }),
    [],
  );
  return user ? (
    <Editor
      key={`${user.id}:${session}`}
      user={user}
      renew={() => {
        credential.current = token();
        setSession((v) => v + 1);
      }}
    />
  ) : loading ? null : (
    <Navigate replace to="/login?redirectUrl=%2Fmypage%2Fedit" />
  );
}
function Editor({ user, renew }) {
  const { locale } = useWebLocale(),
    { resolvedMode } = useColorScheme(),
    en = locale === 'en',
    dark = resolvedMode === 'dark';
  const t = (k, vars = {}) =>
    String(copy[en ? 'en' : 'ko'][k] || k).replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? '');
  const colors = baseline.tokens[dark ? 'darkColors' : 'lightColors'];
  const location = useLocation();
  const sessionToken = useRef(token());
  const navigate = useNavigate(),
    client = useQueryClient();
  const original = useRef(profileDraft(user)),
    [draft, setDraft] = useState(original.current),
    [profile, setProfile] = useState(user),
    [support, setSupport] = useState(null),
    [catalog, setCatalog] = useState(null),
    [loadError, setLoadError] = useState(false),
    [loading, setLoading] = useState(true),
    [attempt, setAttempt] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [imageMenu, setImageMenu] = useState(null),
    [crop, setCrop] = useState(null),
    [colorOpen, setColorOpen] = useState(false),
    [lines, setLines] = useState(1);
  const measurement = useRef(null),
    input = useRef(null),
    target = useRef(null);
  const imageAnchor = useRef(null);
  const openImage = (kind, event) => {
    const field = kind === 'profile' ? 'profileImageUrl' : 'profileBackgroundImageUrl';
    target.current = kind;
    if (!draft[field]) {
      input.current?.click();
      return;
    }
    imageAnchor.current = event.currentTarget;
    setImageMenu(kind);
  };
  const scope = useRef({ alive: true, revision: 0, requests: new Set(), locked: false });
  const guard = useRef({ dirty: false, busy: false, released: false });
  guard.current.dirty = JSON.stringify(draft) !== JSON.stringify(original.current);
  guard.current.busy = busy || !!crop;
  useEffect(() => {
    const s = scope.current;
    s.alive = true;
    const cancel = () => {
      s.revision++;
      s.requests.forEach((c) => c.abort());
      guard.current.released = true;
    };
    const off = useUserStore.subscribe((n, p) => {
      if (
        n.user?.id !== p.user?.id ||
        n.isLogin !== p.isLogin ||
        n.isAdmin !== p.isAdmin ||
        sessionToken.current !== token()
      )
        cancel();
    });
    return () => {
      s.alive = false;
      cancel();
      off();
    };
  }, []);
  const request = () => {
    const s = scope.current,
      revision = s.revision,
      key = token(),
      c = new AbortController();
    s.requests.add(c);
    const same = () =>
      s.alive &&
      revision === s.revision &&
      !c.signal.aborted &&
      useUserStore.getState().user?.id === user.id;
    const current = () => same() && token() === key;
    return {
      current,
      cancel: () => c.abort(),
      options: { signal: c.signal, suppressGlobalError: true, isSessionCurrent: current },
      finish: () => {
        s.requests.delete(c);
        if (same() && token() !== key) {
          s.revision++;
          s.requests.forEach((x) => x.abort());
          renew();
        }
      },
    };
  };
  useEffect(() => {
    const r = request();
    setLoading(true);
    setLoadError(false);
    Promise.allSettled([
      api.getAuth('/sponsorships/me', r.options),
      api.getPublic('/sponsorships/catalog', { ...r.options, params: { locale } }),
      api.getAuth(`/users/${encodeURIComponent(user.nickname)}`, r.options),
    ])
      .then((results) => {
        if (!r.current()) return;
        const [s, c, p] = results;
        if (s.status === 'fulfilled') setSupport(unwrap(s.value));
        else setLoadError(true);
        if (c.status === 'fulfilled') setCatalog(unwrap(c.value));
        if (p.status === 'fulfilled') setProfile(unwrap(p.value));
        else setLoadError(true);
      })
      .finally(() => {
        if (r.current()) setLoading(false);
        r.finish();
      });
    return () => r.cancel();
    // Only a new account/remount or explicit retry reloads saved metadata, never the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);
  useLayoutEffect(() => {
    const node = measurement.current;
    if (!node) return;
    let alive = true;
    const measure = () => {
      if (alive) {
        const line = parseFloat(getComputedStyle(node).lineHeight) || 21;
        setLines(Math.max(1, Math.round(node.getBoundingClientRect().height / line)));
      }
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure);
    return () => {
      alive = false;
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [draft.bio]);
  const invalidate = () => {
    void refreshBenefitQueries(client);
  };
  const run = async (work, success, failure) => {
    if (scope.current.locked) return;
    if (sessionToken.current !== token()) {
      guard.current.released = true;
      renew();
      return;
    }
    scope.current.locked = true;
    setBusy(true);
    setError('');
    const r = request();
    try {
      const result = await work(r.options);
      if (r.current()) success(unwrap(result));
    } catch (e) {
      if (r.current()) setError(failure);
    } finally {
      scope.current.locked = false;
      if (r.current()) setBusy(false);
      r.finish();
    }
  };
  const save = () => {
    if (scope.current.locked) return;
    if (lines > 3) {
      setError(t('profileEdit.bioLimitBody'));
      return;
    }
    if (!validNickname(draft.nickname)) {
      setError(t('profileEdit.nicknameRule', { max: 14 }));
      return;
    }
    const payload = profilePayload(draft, original.current);
    void run(
      (options) => api.putAuth('/me', payload, options),
      () => {
        useUserStore.setState((s) => ({ user: { ...s.user, ...profileDraft(payload) } }));
        invalidate();
        guard.current.released = true;
        if (location.state?.profileEditorOrigin) navigate(-1);
        else navigate('/mypage', { replace: true });
      },
      t('profileEdit.saveFailed'),
    );
  };
  const chooseFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('profileEdit.imageOnly'));
      return;
    }
    if (file.size > MAX_PROFILE_IMAGE_BYTES) {
      setError(t('profileEdit.imageTooLarge'));
      return;
    }
    setCrop({ file, target: target.current });
  };
  const upload = (file) => {
    const field = crop.target === 'profile' ? 'profileImageUrl' : 'profileBackgroundImageUrl';
    setCrop(null);
    const form = new FormData();
    form.append('image', file);
    void run(
      (options) => api.postAuth('/images/upload', form, options),
      (data) => {
        if (typeof data?.url !== 'string' || !data.url) {
          setError(t('profileEdit.uploadFailed'));
          return;
        }
        setDraft((d) => ({ ...d, [field]: data.url }));
      },
      t('profileEdit.uploadFailed'),
    );
  };
  const options = en
    ? palettes.en
    : (catalog?.policy?.nicknameColors || palettes.ko).map(
        (c) => palettes.ko.find((p) => p.key === c.key) || c,
      );
  const entitled = support?.nicknameColorEntitled === true;
  const selected = entitled
    ? options.find((c) => c.key === support?.preferences?.selectedNicknameColorKey)
    : null;
  const chooseColor = (key) => {
    if (!entitled || scope.current.locked) return;
    setColorOpen(false);
    const preferences = {
      ...support.preferences,
      selectedNicknameColorKey: key,
      supporterRosterVisible:
        support.preferences?.supporterRosterVisible ?? support.adminBenefitsAccess ?? false,
    };
    void run(
      (o) => api.putAuth('/sponsorships/me/preferences', preferences, o),
      (data) => {
        const next = data?.preferences ? data : { ...support, preferences };
        setSupport(next);
        const color = next.nicknameColorEntitled
          ? (options.find((option) => option.key === next.preferences.selectedNicknameColorKey) ??
            null)
          : null;
        useUserStore.setState((state) => ({
          user: {
            ...state.user,
            profileMark: { ...state.user.profileMark, nicknameColor: color },
          },
        }));
        invalidate();
      },
      t('sponsor.preferenceFailed'),
    );
  };
  const preview = {
    ...profile,
    ...draft,
    profileMark: { ...profile.profileMark, nicknameColor: selected },
  };
  return (
    <PageLayout paddingTop={0}>
      <div
        className="pp-profile-editor"
        style={{
          '--edit-text': colors.text,
          '--edit-secondary': colors.textSecondary,
          '--edit-border': colors.divider,
          '--edit-surface': colors.surfaceSoft,
        }}
      >
        <PlaylistEditorGuard state={guard} en={en} />
        <MyProfileHero
          profile={preview}
          editing
          busy={busy || !!crop}
          onBack={() =>
            location.state?.profileEditorOrigin || window.history.state?.idx > 0
              ? navigate(-1)
              : navigate('/mypage', { replace: true })
          }
          onSave={save}
          onProfileImage={(event) => openImage('profile', event)}
          onBackgroundImage={(event) => openImage('background', event)}
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
          className="pp-profile-edit-form"
        >
          <div className="pp-profile-field">
            <div className="pp-profile-label">
              <label htmlFor="profile-nickname">{t('profileEdit.nickname')}</label>
              <span>{Math.min(nicknameCharacters(draft.nickname).length, 14)}/14</span>
            </div>
            <input
              id="profile-nickname"
              autoComplete="nickname"
              maxLength={15}
              value={draft.nickname}
              disabled={busy}
              onChange={(e) => setDraft((d) => ({ ...d, nickname: e.target.value }))}
              onBlur={() => setDraft((d) => ({ ...d, nickname: limitNickname(d.nickname) }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
            />
          </div>
          <div className="pp-profile-field">
            <div className="pp-profile-label">
              <label htmlFor="profile-bio">{t('profileEdit.bio')}</label>
              <span>{t('profileEdit.bioLimit', { lines: draft.bio ? lines : 0 })}</span>
            </div>
            <div className="pp-profile-bio-wrap">
              <textarea
                id="profile-bio"
                maxLength={300}
                rows={3}
                value={draft.bio}
                disabled={busy}
                placeholder={t('profileEdit.bioPlaceholder')}
                aria-invalid={lines > 3}
                aria-describedby={lines > 3 ? 'profile-bio-error' : undefined}
                onChange={(e) => setDraft((d) => ({ ...d, bio: e.target.value }))}
              />
              <div ref={measurement} className="pp-profile-bio-measure" aria-hidden="true">
                {draft.bio || ' '}
                {'\u200b'}
              </div>
            </div>
            {lines > 3 && (
              <p role="alert" id="profile-bio-error">
                {t('profileEdit.bioLimitMessage')}
              </p>
            )}
          </div>
          {entitled && (
            <button
              type="button"
              className="pp-profile-color-row"
              onClick={() => setColorOpen(true)}
              disabled={busy}
            >
              <span>
                <strong>{t('sponsor.colorNickname')}</strong>
                <small>{selected?.name || t('sponsor.colorDefault')}</small>
              </span>
              {selected && <i style={{ background: selected[dark ? 'dark' : 'light'] }} />}
              <FiChevronRight size={18} />
            </button>
          )}
        </form>
        {loading && <p role="status">{t('common.loading')}</p>}
        {loadError && (
          <div role="alert">
            <p>
              {en
                ? 'Could not load profile options. Your draft is preserved.'
                : '프로필 설정을 불러오지 못했어요. 작성 중인 내용은 유지됩니다.'}
            </p>
            <button
              type="button"
              disabled={loading || busy}
              onClick={() => setAttempt((n) => n + 1)}
            >
              {t('common.retry')}
            </button>
          </div>
        )}
        {error && <p role="alert">{error}</p>}
        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          aria-label={t('profileEdit.changePhoto')}
          onChange={chooseFile}
        />
        {imageMenu && (
          <Popover
            open
            className="pp-review-dialog pp-profile-image-dialog"
            anchorEl={imageAnchor.current}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            marginThreshold={16}
            PaperProps={{ role: 'dialog', 'aria-labelledby': 'profile-image-title' }}
            onClose={() => setImageMenu(null)}
            aria-labelledby="profile-image-title"
          >
            <DialogTitle id="profile-image-title">
              {t(
                imageMenu === 'profile'
                  ? 'profileEdit.profilePhoto'
                  : 'profileEdit.backgroundPhoto',
              )}
            </DialogTitle>
            <DialogContent>
              <Button
                onClick={() => {
                  target.current = imageMenu;
                  setImageMenu(null);
                  input.current?.click();
                }}
              >
                {t('profileEdit.changePhoto')}
              </Button>
              {draft[imageMenu === 'profile' ? 'profileImageUrl' : 'profileBackgroundImageUrl'] && (
                <Button
                  onClick={() => {
                    const field =
                      imageMenu === 'profile' ? 'profileImageUrl' : 'profileBackgroundImageUrl';
                    setDraft((d) => ({ ...d, [field]: null }));
                    setImageMenu(null);
                  }}
                >
                  {t('profileEdit.deletePhoto')}
                </Button>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setImageMenu(null)}>{t('common.cancel')}</Button>
            </DialogActions>
          </Popover>
        )}
        {crop && (
          <ProfileImageCrop
            file={crop.file}
            ratio={crop.target === 'profile' ? 1 : 16 / 9}
            en={en}
            onCancel={() => setCrop(null)}
            onUse={upload}
          />
        )}
        {colorOpen && entitled && (
          <Dialog
            open
            fullWidth
            maxWidth="xs"
            className="pp-review-dialog"
            aria-labelledby="profile-color-title"
            onClose={() => setColorOpen(false)}
          >
            <DialogTitle id="profile-color-title">{t('sponsor.colorNickname')}</DialogTitle>
            <DialogContent>
              <div role="radiogroup" aria-label={t('sponsor.colorNickname')}>
                {[{ key: null, name: t('sponsor.colorDefault') }, ...options].map((c) => (
                  <button
                    className="pp-profile-color-choice"
                    type="button"
                    role="radio"
                    aria-checked={(support.preferences?.selectedNicknameColorKey ?? null) === c.key}
                    key={c.key || 'default'}
                    onClick={() => chooseColor(c.key)}
                    disabled={busy}
                  >
                    <i style={{ background: c[dark ? 'dark' : 'light'] || 'transparent' }} />
                    <span style={{ color: c[dark ? 'dark' : 'light'] }}>{c.name}</span>
                    {(support.preferences?.selectedNicknameColorKey ?? null) === c.key ? '✓' : ''}
                  </button>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setColorOpen(false)}>{t('common.cancel')}</Button>
            </DialogActions>
          </Dialog>
        )}
      </div>
    </PageLayout>
  );
}
